import { useMemo } from 'react';
import { useQuery } from 'react-query';
import HouseholdService from 'services/householdService';
import useAbortController from 'hooks/useAbortController';
import { QK_HOUSEHOLD_UNIT_ACCESSIBILITY } from './constants';

/**
 * ADA status and accessibility types of the household's unit.
 *
 * The server decides whether there is anything to show — the feature flag, a
 * household with no unit, and a prior resident all come back as an empty object
 * — so this hook never second-guesses it and the two profiles cannot apply
 * different rules.
 */
export const useFetchHouseholdUnitAccessibility = (
  organizationId: string,
  propertyId: string,
  householdId: string,
  enabled: boolean = true,
) => {
  const householdService = useMemo(() => new HouseholdService(), []);
  const abortControllerOptions = useAbortController();

  return useQuery({
    // Scoped by org and property, not just the household: the response is
    // property-specific, so a key on the household alone would let one
    // property's answer be served for another.
    queryKey: [
      QK_HOUSEHOLD_UNIT_ACCESSIBILITY,
      organizationId,
      propertyId,
      householdId,
    ],
    queryFn: () =>
      householdService.getHouseholdUnitAccessibility(
        organizationId,
        propertyId,
        householdId,
        abortControllerOptions,
      ),
    enabled: Boolean(enabled && organizationId && propertyId && householdId),
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  });
};
